import { CaretDown, DotsThreeVertical } from "@phosphor-icons/react";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import { getPluralizedWord } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { getLeadListLastMinedAt } from "../../utils";
import LeadListActions from "../AllLists/LeadListActions";

import type { LeadList } from "../../types";

interface LeadListHeadingProps {
  leadList?: LeadList;
}

export default function LeadListHeading({ leadList }: LeadListHeadingProps) {
  const isDesktop = useTwBreakpoint("lg");

  if (!leadList) {
    if (!isDesktop) {
      return (
        <div className="flex flex-col gap-2.5">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-56" />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-4 w-56" />
      </div>
    );
  }

  const { lead_count: leadCount, sources, name } = leadList;

  const updatedAt = getLeadListLastMinedAt(sources);

  const listStatsContent = (
    <div className="flex flex-row gap-2.5 items-center text-xs-medium text-black-400">
      <div className="flex flex-row gap-1.5 items-center">
        {/* Display a green dot if there are leads */}
        <RenderIf condition={leadCount > 0}>
          <div className="p-1 w-fit rounded-full bg-green-100">
            <div className="rounded-full bg-green-500 size-1.25"></div>
          </div>
        </RenderIf>

        <span>
          {leadCount} {getPluralizedWord(leadCount, "lead")}
        </span>
      </div>
      •
      <span>
        {sources.length} {getPluralizedWord(sources.length, "source")}
      </span>
      {updatedAt && (
        <>
          •
          <span>
            updated{" "}
            {new Date(updatedAt).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </>
      )}
    </div>
  );

  if (!isDesktop) {
    return (
      <div className="flex flex-row gap-2 items-start justify-between">
        <div className="flex flex-col gap-2.5">
          <span className="text-xl-bold truncate max-w-[25ch]">{name}</span>
          {listStatsContent}
        </div>

        <LeadListActions leadList={leadList}>
          <Button intent="iconOnly" variant="outline-black">
            <DotsThreeVertical />
          </Button>
        </LeadListActions>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <LeadListActions
        leadList={leadList}
        contentClassName="min-w-[var(--radix-dropdown-menu-trigger-width)]"
      >
        <button
          className="group hover:bg-black-100 data-[state=open]:bg-black-100 rounded-lg w-fit text-md-bold transition-colors duration-150 flex flex-row p-1 -ml-1 gap-1.5 items-center"
          type="button"
        >
          {name}
          <CaretDown className="group-hover:text-black-950 group-data-[state=open]:text-black-950 group-data-[state=open]:rotate-180 text-black-400 size-4 transition-transform" />
        </button>
      </LeadListActions>

      {listStatsContent}
    </div>
  );
}
